import type { GiftCertificate } from '@bulknutrients/bulk-shared-lib/interfaces/ICart';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { Icons } from '@components/Icons/Icons';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useRemoveItem } from '@hooks/cart/useRemoveItem';
import { log } from '@lib/logs';
import Image from 'next/legacy/image';
import Link from 'next/link';
import { type ReactElement, useState } from 'react';
import { twJoin } from 'tailwind-merge';
import { CartPrice } from './CartPrice';

export function CartGiftCertificate({
  certificate,
}: {
  certificate: GiftCertificate;
}): ReactElement {
  // Hooks from BigCommerce
  const removeItem = useRemoveItem({
    include: ['line_items.physical_items.options'],
  });

  // Local state
  const [updating, setUpdating] = useState(false); // used for setting whole item loading on remove
  /**
   * We need to do this because the type is wrong otherwise
   * Putting this in state rather than a const allows us to set it to null
   * Which allows the hack of showing an empty div if null, after removed from cart.
   * This is needed because forceUpdate was here...but not working ¯\_(ツ)_/¯
   */
  const [cert, setCert] = useState<GiftCertificate | null>({
    ...certificate,
  });

  const handleRemove = async () => {
    setUpdating(true);

    try {
      await removeItem({ id: certificate.id });
    } catch (error) {
      log({ error, location: 'CartGiftCertificate handleRemove' });
    } finally {
      setCert(null);
    }
  };

  if (!cert) return <div />;

  return (
    <div
      className={twJoin(['mx-auto my-0 px-0 py-4', updating && 'opacity-50'])}
    >
      <button
        type="button"
        onClick={handleRemove}
        className="float-right border-none bg-white px-2.5 py-1 uppercase text-grey-dark hover:bg-orange hover:text-white dark:bg-transparent dark:text-white dark:hover:bg-orange dark:hover:text-white"
      >
        <Icons.Trash size={20} />
      </button>
      <h2 className="flex gap-1 py-2 leading-5">
        <TextLink href="/giftcertificates" target="_top">
          {cert.name}
        </TextLink>
        <span>for {cert.recipient.name}</span>
      </h2>
      <section className="grid grid-cols-[150px_auto] gap-6">
        <Link
          prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
          href="/giftcertificates"
        >
          <figure
            style={{
              margin: '0px auto',
              width: 'fit-content',
            }}
          >
            <Image
              src="/images/Bulk-Nutrients-gift-certificate.webp"
              alt="gift certificate render "
              width={150}
              height={150}
              className="object-scale-down object-center"
            />
          </figure>
        </Link>
        <div className="m-0">
          <Paragraph className="hidden sm:block">{cert.message}</Paragraph>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
            <div className="flex flex-col font-secondary">
              <span className="font-secondary text-sm uppercase tracking-wide text-grey-mid">
                Quantity:
              </span>
              <span>1</span>
            </div>
            <CartPrice retail={cert.amount} discounted={cert.amount} />
          </div>
        </div>
      </section>
      <HorizontalDivider className="via-grey-mid" />
    </div>
  );
}
